import React, { useState, useMemo } from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Banknote,
  Check,
  Clock,
  Landmark,
  MessageCircle,
  Plus,
  Save,
  Wallet,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Btn, Busca, Field, Modal, Pill, Stat, Vazio, usarServidor } from "../ui/componentes";
import { FORMAS_ESCOLHIVEIS, HOJE, ROTULO_FORMA, atrasado, brl, dias, fdata } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   FINANCEIRO
   ══════════════════════════════════════════════════════════════════ */
function Financeiro({ db, set, toast, acoes }) {
  const { executar, ocupado } = usarServidor(toast);
  const [aba, setAba] = useState("receber");
  /* A lista só filtrava por texto. Com 87 títulos e 29 vencidos, dar baixa
     nos vencidos era caçar linha a linha lendo a etiqueta de cada uma — e o
     aviso do topo dizia o número sem levar a lugar nenhum. */
  const [situacao, setSituacao] = useState("todos");
  const [q, setQ] = useState("");
  const [novo, setNovo] = useState(null);
  const [nf, setNf] = useState({ desc: "", valor: "", venc: dias(7), forma: "pix", cli: "" });

  const receber = db.fin.filter((f) => f.tipo === "receber");
  const pagar = db.fin.filter((f) => f.tipo === "pagar");
  const aReceber = receber.filter((f) => !f.pago).reduce((s, f) => s + f.valor, 0);
  const aPagar = pagar.filter((f) => !f.pago).reduce((s, f) => s + f.valor, 0);
  const recebido = receber.filter((f) => f.pago).reduce((s, f) => s + f.valor, 0);
  const pago = pagar.filter((f) => f.pago).reduce((s, f) => s + f.valor, 0);
  const vencidos = db.fin.filter((f) => !f.pago && atrasado(f.venc));

  // Baixa parcial é a regra, não exceção: o cliente deixa metade e volta na
  // sexta. Por isso vai o saldo, não o valor original do título.
  const baixar = (id) => {
    const t = db.fin.find((f) => f.id === id);
    executar(
      () => acoes.baixarTitulo(id, t.saldo ?? t.valor, t.forma ?? "pix"),
      () => toast(t.tipo === "receber" ? "Recebimento registrado" : "Pagamento registrado",
        `${brl(t.saldo ?? t.valor)} — ${t.desc}`),
      "Não deu para baixar");
  };

  const salvarLanc = () => executar(
    () => {
      if (!nf.desc.trim() || !+nf.valor) throw new Error("Todo lançamento precisa de descrição e valor.");
      return acoes.criarLancamento({ ...nf, tipo: novo });
    },
    () => {
      setNovo(null); setNf({ desc: "", valor: "", venc: dias(7), forma: "pix", cli: "" });
      toast("Lançamento criado", `${brl(+nf.valor)} em contas a ${novo}.`);
    },
    "Não deu para lançar");

  /**
   * Fluxo de caixa dos últimos seis meses, do que foi realmente quitado.
   *
   * Era derivado do array FATURAMENTO do protótipo, com as saídas estimadas
   * por uma fórmula — `* (0.61 + (i % 3) * 0.045)`. Ou seja: número inventado
   * a partir de outro número inventado, numa tela de dinheiro.
   *
   * Agora conta lançamento quitado, agrupado pelo mês da quitação.
   */
  const fluxo = useMemo(() => {
    const NOMES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    const base = HOJE();
    const meses = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
      meses.push({
        chave: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        mes: NOMES[d.getMonth()], entradas: 0, saidas: 0,
      });
    }
    const porChave = Object.fromEntries(meses.map((m) => [m.chave, m]));
    for (const f of db.fin) {
      if (!f.pago) continue;
      const alvo = porChave[f.pago.slice(0, 7)];
      if (!alvo) continue;
      if (f.tipo === "receber") alvo.entradas += f.valor; else alvo.saidas += f.valor;
    }
    return meses;
  }, [db.fin]);

  /* Varre o enum inteiro. Antes eram cinco chaves escritas à mão, e uma
     delas — "cartao" — não existe no banco: toda a receita de débito e de
     crédito ficava fora deste gráfico, calada. `filter(v > 0)` já esconde o
     que não teve movimento, então varrer tudo não polui nada. */
  const porForma = Object.keys(ROTULO_FORMA)
    .map((k) => ({ nome: ROTULO_FORMA[k], v: receber.filter((f) => f.pago && f.forma === k).reduce((s, f) => s + f.valor, 0) }))
    .filter((x) => x.v > 0);
  const CORES = ["#171C22", "#4FAFD1", "#E2620C", "#137A56", "#8D9AA6"];

  const listaAtual = (aba === "receber" ? receber : pagar)
    .filter((f) => !q || f.desc.toLowerCase().includes(q.toLowerCase()))
    .filter((f) => situacao === "todos"
      || (situacao === "aberto"  && !f.pago)
      || (situacao === "vencido" && !f.pago && atrasado(f.venc))
      || (situacao === "quitado" && f.pago));

  return (
    <div className="grid gap-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
        <Stat label="Recebido no período" value={brl(recebido)} tone="ok" icon={ArrowDown} delta={9} />
        <Stat label="A receber" value={brl(aReceber)} icon={Wallet} sub={`${receber.filter((f) => !f.pago).length} título(s) em aberto`} />
        <Stat label="A pagar" value={brl(aPagar)} tone="signal" icon={ArrowUp} sub={`${pagar.filter((f) => !f.pago).length} conta(s) em aberto`} />
        <Stat label="Resultado do caixa" value={brl(recebido - pago)} tone={recebido - pago >= 0 ? "ok" : "crit"} icon={Landmark} sub="entradas menos saídas já quitadas" />
      </div>

      {vencidos.length > 0 && (
        <div className="card p-3.5 flex flex-wrap items-center gap-3" style={{ borderLeft: "3px solid var(--crit)" }}>
          <AlertTriangle size={16} className="t-crit" />
          <span className="text-sm flex-1"><span className="font-semibold">{vencidos.length} título(s) vencido(s)</span> somando {brl(vencidos.reduce((s, f) => s + f.valor, 0))}. Cobre ou renegocie antes que vire perda.</span>
          {/* Cobrança em massa não existe: o WhatsApp abre uma conversa por
              vez, e disparo automático precisaria de integração paga. Antes
              este botão dizia que todos tinham recebido lembrete. */}
          {/* O número do aviso agora leva aos títulos. Dizer "29 vencidos" e
              deixar a pessoa procurar os 29 no meio de 87 é dar o susto sem
              dar o caminho. */}
          <Btn variant="o" size="xs" icon={Wallet}
            onClick={() => { setAba("receber"); setSituacao("vencido"); setQ(""); }}>Ver os vencidos</Btn>
          <Btn variant="o" size="xs" icon={MessageCircle}
            onClick={() => toast("Um de cada vez, por enquanto",
              "O WhatsApp abre uma conversa por vez. Abra o cliente e fale por lá.", "alerta")}>Cobrar todos</Btn>
        </div>
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))" }}>
        <div className="card p-4">
          <div className="flex items-baseline justify-between mb-3">
            <span className="dsp" style={{ fontSize: 15 }}>Fluxo de caixa</span><span className="text-xs t-mut2">entradas × saídas</span>
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <AreaChart data={fluxo} margin={{ top: 4, right: 4, left: -14, bottom: 0 }}>
              <defs>
                <linearGradient id="ge" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4FAFD1" stopOpacity={0.32} /><stop offset="100%" stopColor="#4FAFD1" stopOpacity={0} /></linearGradient>
                <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E2620C" stopOpacity={0.26} /><stop offset="100%" stopColor="#E2620C" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="#DCE1E6" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#697987" }} axisLine={{ stroke: "#DCE1E6" }} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#8D9AA6" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v, n) => [brl(v), n === "entradas" ? "Entradas" : "Saídas"]} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #DCE1E6" }} />
              <Area type="monotone" dataKey="entradas" stroke="#2C7E9C" strokeWidth={2} fill="url(#ge)" />
              <Area type="monotone" dataKey="saidas" stroke="#E2620C" strokeWidth={2} fill="url(#gs)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card p-4">
          <span className="dsp block mb-3" style={{ fontSize: 15 }}>Como o cliente paga</span>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="52%" height={196}>
              <PieChart><Pie data={porForma} dataKey="v" nameKey="nome" innerRadius={42} outerRadius={70} paddingAngle={2} stroke="none">
                {porForma.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}</Pie>
                <Tooltip formatter={(v) => brl(v)} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #DCE1E6" }} /></PieChart>
            </ResponsiveContainer>
            <div className="flex-1 grid gap-1.5">
              {porForma.map((f, i) => (
                <div key={f.nome} className="flex items-center gap-2 text-sm">
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: CORES[i % CORES.length], flex: "none" }} />
                  <span className="flex-1 t-mut">{f.nome}</span><span className="mono font-semibold">{brl(f.v)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 flex-1" style={{ borderBottom: "1px solid var(--line)" }}>
          <button className={`tab ${aba === "receber" ? "on" : ""}`} onClick={() => setAba("receber")}>Contas a receber</button>
          <button className={`tab ${aba === "pagar" ? "on" : ""}`} onClick={() => setAba("pagar")}>Contas a pagar</button>
        </div>
        {/* Em aberto e Vencidos são o que a oficina abre a tela para fazer:
            saber o que falta receber e cobrar quem atrasou. */}
        <div className="flex gap-1">
          {[["todos", "Todos"], ["aberto", "Em aberto"], ["vencido", "Vencidos"], ["quitado", "Quitados"]].map(([v, l]) => (
            <button key={v} className={`tab ${situacao === v ? "on" : ""}`}
              style={{ fontSize: 12, padding: "4px 10px" }}
              onClick={() => setSituacao(v)}>{l}</button>
          ))}
        </div>
        <Busca v={q} set={setQ} ph="Buscar lançamento…" />
        <Btn variant="p" icon={Plus} onClick={() => setNovo(aba)}>Novo lançamento</Btn>
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead><tr><th>Descrição</th><th>Cliente / OS</th><th>Vencimento</th><th>Forma</th><th>Situação</th><th className="num">Valor</th><th></th></tr></thead>
            <tbody>
              {listaAtual.map((f) => {
                const atr = !f.pago && atrasado(f.venc);
                return (
                  <tr key={f.id} style={atr ? { background: "var(--crit-l)" } : undefined}>
                    <td className="text-sm font-medium">{f.desc}</td>
                    <td className="text-sm t-mut">{db.clientes.find((c) => c.id === f.cli)?.nome || "—"}{f.os && <span className="block text-xs mono t-mut2">{f.os}</span>}</td>
                    <td className="text-sm mono">{fdata(f.venc)}</td>
                    <td className="text-sm t-mut">{ROTULO_FORMA[f.forma] ?? f.forma ?? "—"}</td>
                    <td>{f.pago ? <Pill t="ok" icon={Check}>Quitado {fdata(f.pago)}</Pill> : atr ? <Pill t="crit" icon={AlertTriangle}>Vencido</Pill> : <Pill t="mut" icon={Clock}>Em aberto</Pill>}</td>
                    <td className="num text-sm font-semibold">{brl(f.valor)}</td>
                    <td style={{ textAlign: "right" }}>
                      {!f.pago && <Btn variant={f.tipo === "receber" ? "ok" : "o"} size="xs" icon={Banknote} onClick={() => baixar(f.id)}>{f.tipo === "receber" ? "Receber" : "Pagar"}</Btn>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {listaAtual.length === 0 && <Vazio icon={Wallet} titulo="Nada lançado aqui" msg="Os títulos aparecem sozinhos quando um orçamento é aprovado ou uma compra é lançada no estoque." />}
      </div>

      <Modal open={!!novo} onClose={() => setNovo(null)} w="max-w-lg" title={novo === "receber" ? "Nova conta a receber" : "Nova conta a pagar"}
        footer={<><Btn variant="g" onClick={() => setNovo(null)}>Cancelar</Btn><Btn variant="p" icon={Save} onClick={salvarLanc}>Lançar</Btn></>}>
        <div className="grid gap-3.5">
          <Field label="Descrição"><input className="inp" value={nf.desc} onChange={(e) => setNf({ ...nf, desc: e.target.value })} placeholder={novo === "receber" ? "Venda balcão — bateria" : "Aluguel do galpão"} /></Field>
          <div className="grid gap-3.5" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <Field label="Valor"><input className="inp inp-mono" value={nf.valor} onChange={(e) => setNf({ ...nf, valor: e.target.value })} placeholder="0,00" /></Field>
            <Field label="Vencimento"><input className="inp" type="date" value={nf.venc} onChange={(e) => setNf({ ...nf, venc: e.target.value })} /></Field>
            <Field label="Forma de pagamento"><select className="inp" value={nf.forma} onChange={(e) => setNf({ ...nf, forma: e.target.value })}>{FORMAS_ESCOLHIVEIS.map((k) => <option key={k} value={k}>{ROTULO_FORMA[k]}</option>)}</select></Field>
            {novo === "receber" && <Field label="Cliente"><select className="inp" value={nf.cli} onChange={(e) => setNf({ ...nf, cli: e.target.value })}><option value="">Sem vínculo</option>{db.clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}</select></Field>}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export { Financeiro };
